import { useEffect, useState } from "react";
import axios from "axios";
import { FileSpreadsheet, RefreshCw } from "lucide-react";
import { Button } from "./ui/button";
import { HsCompare } from "./HsCompare";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ago = (iso) => { const m = Math.round((Date.now() - new Date(iso).getTime()) / 60000); return m < 1 ? "الآن" : m < 60 ? `قبل ${m} د` : m < 1440 ? `قبل ${Math.round(m / 60)} س` : new Date(iso).toLocaleDateString("ar-SA"); };
const range = (i) => !i.date_from ? "بدون تاريخ" : i.date_from === i.date_to ? i.date_from : `${i.date_from} ← ${i.date_to}`;

const Table = ({ sel }) => (
  <div className="max-h-[480px] overflow-auto rounded-xl border border-slate-200" data-testid="hs-report-table">
    <table className="w-full text-xs" dir="ltr">
      <thead className="sticky top-0 bg-[#7c2d12] text-white">
        <tr>{sel.rider_col != null && <th className="px-2 py-2 text-right">المندوب</th>}{sel.headers.map((h, i) => <th key={i} className="whitespace-nowrap px-2 py-2 text-left">{h}</th>)}</tr>
      </thead>
      <tbody>
        {sel.rows.map((r, i) => (
          <tr key={i} className="odd:bg-white even:bg-slate-50">
            {sel.rider_col != null && <td className="whitespace-nowrap px-2 py-1.5 text-right font-medium text-slate-700" dir="rtl">{sel.names?.[r[sel.rider_col]] || "—"}</td>}
            {sel.headers.map((_, j) => <td key={j} className="whitespace-nowrap px-2 py-1.5 text-slate-700">{r[j]}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

const tabKey = (i) => (i.kind === "other" ? `other:${i.page_name}` : i.kind);
const same = (i, p) => tabKey(i) === p.key;

export const HsReportsSection = ({ only, exclude = [], title = "تقارير هنقرستيشن 3PL (مسحوبة تلقائياً)" }) => {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState(null);
  const keep = (i) => (only ? only.includes(i.kind) : !exclude.includes(i.kind));
  const toPick = (i) => ({ key: tabKey(i), kind: i.kind, page_name: i.page_name, date_from: i.date_from });
  const load = (p = pick) => axios.get(`${API}/riders/hs-reports`, { params: p ? { kind: p.kind, date_from: p.date_from, page_name: p.page_name } : {} }).then((r) => {
    setData(r.data);
    const first = r.data.items.find(keep);
    if (!p && first) setPick(toPick(first));
  }).catch(() => {});
  useEffect(() => { load(pick); }, [pick]); // eslint-disable-line react-hooks/exhaustive-deps
  const items = (data?.items || []).filter(keep);
  if (!items.length) return null;
  const tabs = items.filter((i, n) => items.findIndex((x) => tabKey(x) === tabKey(i)) === n);
  const sel = data.selected;
  return (
    <section className="mt-6 space-y-3 rounded-2xl border border-orange-200 bg-orange-50/40 p-4" data-testid={`hs-reports-section${only ? `-${only.join("-")}` : ""}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 text-base font-bold text-slate-900"><FileSpreadsheet className="h-5 w-5 text-orange-600" /> {title}</h3>
        <Button size="sm" variant="ghost" onClick={() => load(pick)} className="gap-1.5" data-testid="hs-reports-refresh"><RefreshCw className="h-4 w-4" /> تحديث</Button>
      </div>
      {tabs.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {tabs.map((i) => (
            <button key={tabKey(i)} type="button" onClick={() => setPick(toPick(i))}
              className={`rounded-full px-3 py-1 text-xs font-semibold transition-colors ${pick?.key === tabKey(i) ? "bg-orange-600 text-white" : "bg-white text-slate-600 hover:bg-orange-100"}`} data-testid={`hs-kind-${tabKey(i)}`}>
              {i.kind === "other" ? i.page_name || data.pages.other : data.pages[i.kind]}
            </button>
          ))}
        </div>
      )}
      {pick && (
        <select value={pick.date_from} onChange={(e) => setPick({ ...pick, date_from: e.target.value })} className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm" data-testid="hs-date-select">
          {items.filter((i) => same(i, pick)).map((i) => <option key={i.date_from} value={i.date_from}>{range(i)} · {ago(i.synced_at)}</option>)}
        </select>
      )}
      {sel && keep(sel) && <>
        {sel.note && <p className="text-xs text-emerald-700" data-testid="hs-report-note">{sel.note}</p>}
        <p className="text-xs text-slate-500">{sel.rows.length} صف · آخر مزامنة {ago(sel.synced_at)}{sel.ext_version ? ` · الإضافة ${sel.ext_version}` : ""}{sel.source === "data" ? " · قراءة كاملة من بيانات التقرير" : sel.source === "screen" ? " · قراءة من الشاشة" : ""}</p>
        {data.latest_ext && sel.ext_version !== data.latest_ext && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs font-medium text-amber-800" data-testid="hs-ext-outdated">
            هذا السحب تم بإصدار قديم من الإضافة{sel.ext_version ? ` (${sel.ext_version})` : ""}. نزّل الإصدار {data.latest_ext} من WorkZone وحدّثه في صفحة إضافات كروم، ثم حدّث صفحة التقرير.
          </p>
        )}
        <HsCompare key={`${pick?.key}-${pick?.date_from}`} pick={pick} />
        <Table sel={sel} />
      </>}
    </section>
  );
};
